import { useSelector } from 'react-redux';
import { 
  selectUserFacilities
} from '../redux/slices/facilitiesSlice';
import { useUserData } from './useUserData';

/**
 * Simplified hook for accessing user and facility data
 * Uses useUserData for user information and Redux for facilities
 */
export const useCache = () => {
  // Get current state from useUserData and Redux
  const { user, defaultFacility, userRoles, role } = useUserData();
  const userFacilities = useSelector(selectUserFacilities);
  
  // Consider facilities loaded when user has defaultFacility
  const facilitiesLoaded = !!defaultFacility;

  return {
    // User data
    currentUser: user,
    currentRole: role,
    
    // Facility data
    userFacilities,
    facilitiesLoaded,
    
    // Utility functions
    getFacilityById: (facilityId) => {
      if (!facilityId) return null;
      return userFacilities.find(f => f.id === facilityId) || null;
    },
    
    getFacilitiesByIds: (facilityIds) => {
      if (!facilityIds || !Array.isArray(facilityIds)) return [];
      return facilityIds
        .map(id => userFacilities.find(f => f.id === id))
        .filter(facility => facility !== null);
    },
    
    getDefaultFacility: () => {
      if (!defaultFacility) return null;
      // defaultFacility is now the complete facility object, extract the ID
      const defaultFacilityId = defaultFacility.id;
      return userFacilities.find(f => f.id === defaultFacilityId) || null;
    },
    
    getPrimaryFacility: () => {
      if (!defaultFacility) return null;
      // defaultFacility is now the complete facility object, extract the ID
      const defaultFacilityId = defaultFacility.id;
      const defaultFacilityObj = userFacilities.find(f => f.id === defaultFacilityId);
      if (defaultFacilityObj) return defaultFacilityObj;
      return userFacilities.length > 0 ? userFacilities[0] : null;
    },
    
    hasFacilityAccess: (facilityId) => {
      if (!facilityId || !user) return false;
      
      // Admin has access to all facilities
      if (userRoles && userRoles.includes('admin')) return true;
      
      // Check if facility is in user's facilities
      return userFacilities.some(f => f.id === facilityId);
    }
  };
};

/**
 * Hook for getting current user from useUserData
 */
export const useCurrentUser = () => {
  const { user, role, defaultFacility } = useUserData();
  
  // Consider facilities loaded when user has defaultFacility
  const facilitiesLoaded = !!defaultFacility;
  
  return {
    user,
    role,
    isLoaded: !!user && facilitiesLoaded
  };
};

/**
 * Hook for getting user facilities from Redux
 */
export const useUserFacilities = () => {
  const userFacilities = useSelector(selectUserFacilities);
  const { defaultFacility } = useUserData();
  
  // Consider facilities loaded when user has defaultFacility
  const facilitiesLoaded = !!defaultFacility;
  
  return {
    facilities: userFacilities,
    isLoaded: facilitiesLoaded,
    count: userFacilities.length
  };
};

/**
 * Hook for getting a specific facility from Redux
 */
export const useFacility = (facilityId) => {
  const { user, userRoles } = useUserData();
  const userFacilities = useSelector(selectUserFacilities);
  
  const facility = userFacilities.find(f => f.id === facilityId) || null;
  const hasAccess = facilityId && user ? 
    (userRoles?.includes('admin') || userFacilities.some(f => f.id === facilityId)) : 
    false;
  
  return {
    facility,
    hasAccess,
    isLoaded: !!facility
  };
};

/**
 * Hook for getting multiple facilities from Redux
 */
export const useFacilities = (facilityIds) => {
  const userFacilities = useSelector(selectUserFacilities);
  
  const facilities = facilityIds ? 
    facilityIds
      .map(id => userFacilities.find(f => f.id === id))
      .filter(facility => facility !== null) : 
    [];
  
  return {
    facilities,
    count: facilities.length
  };
};

/**
 * Hook for getting default/primary facility from useUserData
 */
export const usePrimaryFacility = () => {
  const { defaultFacility: userDefaultFacility } = useUserData();
  const userFacilities = useSelector(selectUserFacilities);
  
  let defaultFacility = null;
  if (userDefaultFacility) {
    // defaultFacility is now the complete facility object, extract the ID
    const defaultFacilityId = userDefaultFacility.id;
    defaultFacility = userFacilities.find(f => f.id === defaultFacilityId);
  }
  
  const primaryFacility = defaultFacility || (userFacilities.length > 0 ? userFacilities[0] : null);
  
  return {
    defaultFacility,
    primaryFacility,
    isLoaded: !!primaryFacility
  };
}; 